import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../HomePage.css";
import "./PageHeader.css";

/**
 * Same header layout as HomePage: 3-column grid with nav centered (not grouped with logo).
 *
 * @param {Array<{ label: string, to: string, className?: string }>} [extraNav] - Extra links after Features…Resources
 * @param {Array<{ label: string, to: string, className?: string }>} [customNav] - If set, replaces default nav entirely (prototype / resource hubs)
 * @param {boolean} [showSkipLink=true]
 * @param {'default'|'hero'} [variant='default'] - hero = transparent bar over the homepage
 */
export default function PageHeader({ extraNav = [], customNav = null, showSkipLink = true, variant = "default" }) {
  const { t } = useTranslation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [heroSolid, setHeroSolid] = useState(false);
  const mobileNavRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);

  const isExternal = (to) => /^https?:\/\//i.test(String(to || "").trim());

  useEffect(() => {
    if (variant !== "hero") {
      setHeroSolid(false);
      return undefined;
    }
    const onScroll = () => {
      const hero = document.querySelector(".homepage-hero");
      const headerOffset = 72;
      const threshold = hero ? Math.max(24, hero.offsetHeight - headerOffset) : 24;
      setHeroSolid(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [variant]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onDocMouseDown = (event) => {
      if (
        mobileNavRef.current?.contains(event.target) ||
        mobileMenuButtonRef.current?.contains(event.target)
      ) {
        return;
      }
      setMobileNavOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileNavOpen(false);
        mobileMenuButtonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileNavOpen]);

  const defaultLinks = [
    { label: t("features") || "Features", to: "/Features", className: "header-Features-btn" },
    { label: t("enterprise") || "Enterprise", to: "/Enterprise", className: "header-Enterprise-btn" },
    { label: t("pricing") || "Pricing", to: "/pricing", className: "header-Pricing-btn" },
    { label: t("resources") || "Resources", to: "/Resources", className: "header-Resources-btn" },
  ];

  const extraMapped = (extraNav || []).map((x) => ({
    label: x.label,
    to: x.to,
    className: x.className || "header-Resources-btn",
  }));

  const navItems =
    customNav && customNav.length > 0
      ? customNav.map((x) => ({
          label: x.label,
          to: x.to,
          className: x.className || "header-Resources-btn",
        }))
      : [...defaultLinks, ...extraMapped];

  return (
    <>
      {showSkipLink ? (
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
      ) : null}
      <header className={`homepage-header${variant === "hero" ? " homepage-header--hero" : ""}${heroSolid ? " homepage-header--solid" : ""}`} role="banner">
        <div className="homepage-header__left">
          <Link to="/" className="header-brand" onClick={() => setMobileNavOpen(false)} aria-label="Zolaid home">
            <img src="/logo.png" alt="" className="header-logo" />
            <span>Zolaid</span>
          </Link>
          <button
            ref={mobileMenuButtonRef}
            type="button"
            className="homepage-mobile-menu-btn"
            aria-expanded={mobileNavOpen}
            aria-controls="page-header-main-nav"
            aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileNavOpen((o) => !o)}
          >
            {mobileNavOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>
        <nav
          id="page-header-main-nav"
          ref={mobileNavRef}
          className={`homepage-header__nav${mobileNavOpen ? " homepage-header__nav--open" : ""}`}
          aria-label="Main navigation"
        >
          {navItems.map(({ label, to, className }) => {
            if (isExternal(to)) {
              return (
                <a
                  key={`${to}-${label}`}
                  href={to}
                  className={className}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setMobileNavOpen(false)}
                >
                  {label}
                </a>
              );
            }
            return (
              <Link key={`${to}-${label}`} to={to} className={className} onClick={() => setMobileNavOpen(false)}>
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="homepage-header__account">
          <Link to="/login" className="header-login-btn" onClick={() => setMobileNavOpen(false)}>
            {t("login") || "Login"}
          </Link>
        </div>
        {mobileNavOpen ? (
          <button
            type="button"
            className="homepage-mobile-nav-overlay"
            aria-label="Close menu"
            tabIndex={-1}
            onClick={() => setMobileNavOpen(false)}
          />
        ) : null}
      </header>
    </>
  );
}
